import {isAxiosError} from "axios";
import type {FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import type {Limit, SortDTO} from "crqlar-react-common/dist/models/common/limit";
import api from "crqlar-react-common/dist/services/api";

/** The formats a list exports as: CSV and Excel (features/export-engine.md in crqlar-api-v3). */
export type ServerTableExportFormat = "csv" | "xlsx";

/** The table's live query, as a list turns it into its own request: what `queryFn` gets, without paging. */
export interface ServerTableExportQuery {
    /** Trimmed search text. */
    searchQuery: string
    /** The table's own advanced-filter rules. */
    filterRules: FilterRule[]
    /** The active sort, translated to its backend field like the table's queries. */
    sorting?: SortDTO
}

export interface ServerTableExportColumn {
    /** The table's column id; the backend knows each exportable list's columns by it. */
    id: string
    /** The header as the table shows it. */
    label: string
}

/** The body of `POST /v3/aggregation/lists/{list}/export?format=csv|xlsx`. */
export interface ServerTableExportBody<TRequest> {
    /** The list's own request DTO; the backend ignores its paging. */
    request: TRequest
    columns: ServerTableExportColumn[]
    title: string
    language: string
    /** How many list rows to export, as the table counts them (a group is one); every row when missing. */
    maxRows?: number
    /** How many list rows to skip first: one page of the table starts at page index × page size. */
    offset?: number
}

/** Which rows and columns an export writes. Missing means every row from the top, with the visible columns. */
export interface ServerTableExportRun {
    maxRows?: number
    offset?: number
    columns?: ServerTableExportColumn[]
}

/** A failed export, with the server's own message when it sent one ("This export has 61,000 rows; …"). */
export class ServerTableExportError extends Error {
    constructor(readonly serverMessage?: string) {
        super(serverMessage ?? "The export failed");
        this.name = "ServerTableExportError";
    }
}

/**
 * The `limit` of an export request. List request DTOs require one; the export ignores the page (the body's `maxRows`
 * says how many rows), so only the sort matters.
 */
export const serverTableExportLimit = (sorting?: SortDTO): Limit => ({
    offset: 0,
    limit: 1,
    sort: sorting ? [sorting] : undefined,
});

/**
 * Exports a list on the server and saves the file under the name the server gives it in `Content-Disposition`, or,
 * should a proxy hide that header, under `<title>-<today>.<format>` the way the server names it.
 * Resolves with the file name.
 */
export const downloadServerTableExport = async <TRequest>(
    list: string,
    format: ServerTableExportFormat,
    body: ServerTableExportBody<TRequest>,
): Promise<string> => {
    try {
        const response = await api.post<Blob>(`/v3/aggregation/lists/${encodeURIComponent(list)}/export`, body, {
            params: {format},
            responseType: "blob",
        });
        const fileName = fileNameFromDisposition(response.headers["content-disposition"])
            ?? fallbackFileName(body.title, format);

        saveBlob(response.data, fileName);
        return fileName;
    } catch (error) {
        throw new ServerTableExportError(await serverMessage(error));
    }
};

const saveBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** `attachment; filename*=UTF-8''gaestebefragung-2026-10-08.csv` or `filename="…"`. */
const fileNameFromDisposition = (header: unknown): string | undefined => {
    if (typeof header !== "string") return undefined;

    const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header)?.[1];
    if (encoded) {
        try {
            return decodeURIComponent(encoded.trim().replace(/^"|"$/g, ""));
        } catch {
            // a malformed escape: fall through to the plain parameter
        }
    }

    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(header);
    const name = (plain?.[2] ?? plain?.[1])?.trim();
    return name || undefined;
};

/** The server's name for a file: the title as a slug (`ä` → `ae`), today, the extension. */
const fallbackFileName = (title: string, format: ServerTableExportFormat) => {
    const slug = title
        .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue")
        .replace(/Ä/g, "Ae").replace(/Ö/g, "Oe").replace(/Ü/g, "Ue")
        .replace(/ß/g, "ss")
        .normalize("NFKD")
        .replace(/[̀-ͯ]+/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60)
        .replace(/-+$/g, "") || "export";
    const today = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");

    return `${slug}-${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}.${format}`;
};

/** A failed blob request carries its JSON error body (`{"message": "…"}`) as a blob too. */
const serverMessage = async (error: unknown): Promise<string | undefined> => {
    if (!isAxiosError(error)) return undefined;

    const data: unknown = error.response?.data;
    try {
        const text = data instanceof Blob ? await blobText(data) : typeof data === "string" ? data : undefined;
        const message = text ? JSON.parse(text)?.message : (data as {message?: unknown} | undefined)?.message;
        return typeof message === "string" && message.trim() ? message : undefined;
    } catch {
        return undefined;
    }
};

const blobText = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
});
